import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useResizeObserver } from 'usehooks-ts';

type UseViewportFillHeightOptions = {
  /** When false, skips measuring and returns undefined. */
  enabled?: boolean;
  /** Pixels to leave below the element (e.g. to clear a bottom inset). */
  bottomInset?: number;
};

/**
 * Returns the pixel height from the top of `targetRef` to the bottom of the
 * viewport (minus `bottomInset`) — i.e. the height a scroll container should
 * take to fill the remaining space and become the sole scroller, without a
 * flex-height chain through its ancestors. `undefined` until measured or when
 * disabled.
 *
 * Measures the element's top relative to the viewport rather than its own size,
 * so it can't be a plain `useResizeObserver` return value. Instead it observes
 * the root `<html>` element (height:100% in src/css/index.css, so it tracks the
 * viewport) purely as a resize trigger, and recomputes on mount / enable.
 */
export const useViewportFillHeight = (
  targetRef: RefObject<HTMLElement>,
  { enabled = true, bottomInset = 0 }: UseViewportFillHeightOptions = {},
): number | undefined => {
  const rootRef = useRef<HTMLElement>(document.documentElement);
  const [height, setHeight] = useState<number>();

  const measure = useCallback((): void => {
    const node = targetRef.current;
    if (!enabled || !node) return;

    const { top } = node.getBoundingClientRect();
    setHeight(Math.max(0, Math.round(window.innerHeight - top - bottomInset)));
  }, [targetRef, enabled, bottomInset]);

  // Initial measure, and whenever `enabled`/`bottomInset` change.
  useEffect(measure, [measure]);

  // Recompute when the viewport resizes (rotation, window resize, drawer show).
  useResizeObserver({ ref: rootRef, onResize: measure });

  return enabled ? height : undefined;
};
